import React from 'react';
import { useTranslation } from 'react-i18next';
import { ControlBarButton } from '@m12s/component-library';
import { user, playRegular, signOut } from 'lib/icons';
import Routes from 'lib/utils/routes';
import { UtilityMenu } from 'components/UtilityMenu';
import useOperatorLogout from 'components/LaborTicket/hooks/useOperatorLogout';
import useSelectOperator from 'lib/hooks/useSelectOperator';
import { useSelector } from 'react-redux';
import { getMachine } from 'lib/selectors/getMachine';
import { useHistory } from 'react-router-dom';
import { getCurrentOperator } from 'lib/selectors/getCurrentOperator';
import { getHasOpenActivitySet } from 'lib/selectors/getHasOpenActivitySet';
import { getCompany } from 'lib/selectors/getCompany';
import { getIsErpDriven } from 'lib/selectors/getIsErpDriven';
import { CategorizeDowntime } from './CategorizeDowntime';
import { SwitchMachine } from './SwitchMachine';
import {
  ControlBarResponsive,
  MobileIcon,
  ResponsiveText,
  Scrollable,
} from '../styled';
/**
 * SignInAndSelectJob component displays different control bar options based on operator sign-in status and ERP configuration.
 *
 * Behavior:
 * - When no operator is signed in and NOT ERP driven: shows sign-in button and optionally machine switching
 * - When no operator is signed in and IS ERP driven: only shows machine switching (if enabled) or nothing
 * - When operator is signed in and NOT ERP driven: shows start job, categorize downtime, and sign out buttons
 * - When operator is signed in and IS ERP driven: shows categorize downtime and sign out buttons (no start job)
 *
 * The component uses React hooks and Redux selectors to access:
 * - machine: Current machine details from getMachine selector
 * - operator: Current operator from getCurrentOperator selector
 * - hasOpenActivitySet: Whether there's an open activity set from getHasOpenActivitySet selector
 * - company: Company settings from getCompany selector (for enableMachineSwitching)
 * - isErpDriven: Whether the system is ERP driven from getIsErpDriven selector
 */
const SignInAndSelectJob = () => {
  const { t } = useTranslation();
  const requestOperatorLogout = useOperatorLogout();
  const { openSelectOperator } = useSelectOperator();
  const history = useHistory();
  const machine = useSelector(getMachine);
  const operator = useSelector(getCurrentOperator);
  const hasOpenActivitySet = useSelector(getHasOpenActivitySet);
  const company = useSelector(getCompany);
  const enableMachineSwitching = company?.machineSwitching === true;
  const isErpDriven = useSelector(getIsErpDriven);

  // Signed Out, no job running, ERP driven
  if (!operator && isErpDriven) {
    return (
      <ControlBarResponsive id="job-control-bar">
        <Scrollable>
          <CategorizeDowntime history={history} machine={machine} />
          {enableMachineSwitching && <SwitchMachine history={history} />}
        </Scrollable>
      </ControlBarResponsive>
    );
  }

  // Signed Out, no job running
  if (!operator)
    return (
      <ControlBarResponsive id="job-control-bar">
        <Scrollable>
          <ControlBarButton
            id="job-control-bar-sign-in"
            onClick={() => {
              if (!hasOpenActivitySet) {
                return openSelectOperator({
                  redirectPath: Routes.machineIdSelectJobPath(machine.id),
                });
              }

              return openSelectOperator();
            }}
          >
            <MobileIcon icon={user} />
            <ResponsiveText
              full={t('signIn|full')}
              medium={t('signIn|medium')}
              short={t('signIn|short')}
            />
          </ControlBarButton>
          {enableMachineSwitching && <SwitchMachine history={history} />}
        </Scrollable>
      </ControlBarResponsive>
    );

  // Signed In, no job running
  return (
    <ControlBarResponsive id="job-control-bar">
      <UtilityMenu />

      <Scrollable>
        {!isErpDriven && (
          <ControlBarButton
            id="job-control-start-job"
            onClick={() => {
              return history.push(Routes.machineIdSelectJobPath(machine.id));
            }}
          >
            <MobileIcon icon={playRegular} />
            <ResponsiveText
              full={t('startJob|full')}
              medium={t('startJob|medium')}
              short={t('startJob|short')}
            />
          </ControlBarButton>
        )}

        <CategorizeDowntime history={history} machine={machine} />

        {!isErpDriven && (
          <ControlBarButton
            id="signout"
            onClick={() => {
              return requestOperatorLogout();
            }}
          >
            <MobileIcon icon={signOut} />
            <ResponsiveText
              full={t('signout|full')}
              medium={t('signout|medium')}
              short={t('signout|short')}
            />
          </ControlBarButton>
        )}
      </Scrollable>
    </ControlBarResponsive>
  );
};

export { SignInAndSelectJob };
